/**
 * MemberService
 * Service for Member entity operations
 * Route: api/members
 * Controller: MembersController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * MemberService
 * Handles member CRUD operations and custom endpoints
 * Maps to /api/members endpoint
 */
export class MemberService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberService.DEFAULT_SERVICE_PATH,
            basePath: MemberService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM QUERY METHODS ====================
    /**
     * Get paged list of members with optional filtering
     * GET /api/members?page={page}&pageSize={pageSize}&isActive={isActive}&typeId={typeId}
     * @param page - Page number (default: 1)
     * @param pageSize - Page size (default: 50)
     * @param isActive - Filter by active status
     * @param typeId - Filter by member type ID
     * @returns Array of members
     */
    async getPaged(page = 1, pageSize = 50, isActive, typeId) {
        const response = await this.getPagedRaw(page, pageSize, isActive, typeId);
        return response.data ?? [];
    }
    /**
     * Get paged list of members (raw)
     * @param page - Page number
     * @param pageSize - Page size
     * @param isActive - Filter by active status
     * @param typeId - Filter by member type ID
     * @returns ResponseData with array of members
     */
    getPagedRaw(page = 1, pageSize = 50, isActive, typeId) {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        if (isActive !== null && isActive !== undefined) {
            params.append('isActive', isActive.toString());
        }
        if (typeId !== null && typeId !== undefined) {
            params.append('typeId', typeId.toString());
        }
        const endpoint = this.getEndpoint('getPaged', `?${params.toString()}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get member by user ID
     * GET /api/members/by-user/{userId}
     * @param userId - User ID
     * @returns Member object
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        if (!response.data) {
            throw new Error(`Member with user ID ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get member by user ID (raw)
     * @param userId - User ID
     * @returns ResponseData with member
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('byUserId', `by-user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get members with birthdays today
     * GET /api/members/birthdays
     * @returns Array of members with birthdays today
     */
    async getBirthdays() {
        const response = await this.getBirthdaysRaw();
        return response.data ?? [];
    }
    /**
     * Get members with birthdays today (raw)
     * @returns ResponseData with array of members
     */
    getBirthdaysRaw() {
        const endpoint = this.getEndpoint('birthdays', 'birthdays');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CUSTOM UPDATE METHODS ====================
    /**
     * Update member active status
     * PATCH /api/members/{id}/status
     * @param id - Member ID
     * @param isActive - New active status
     * @returns Updated member
     */
    async updateStatus(id, isActive) {
        const response = await this.updateStatusRaw(id, isActive);
        if (!response.data) {
            throw new Error(`Failed to update member ${id} status`);
        }
        return response.data;
    }
    /**
     * Update member status (raw)
     * @param id - Member ID
     * @param isActive - New active status
     * @returns ResponseData with updated member
     */
    updateStatusRaw(id, isActive) {
        const endpoint = this.getEndpoint('updateStatus', `${id}/status`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, isActive);
    }
    // ==================== MODELVIEW METHODS ====================
    /**
     * Get member with enriched model view by ID (cache-first)
     * GET /api/members/view/{id}
     * @param id - Member ID
     * @returns Enriched member model view with type and marital status
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Member view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get member view by ID (raw)
     * @param id - Member ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all member views (cache-first, enriched with type and marital status)
     * GET /api/members/view
     * @returns Array of enriched member model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all member views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered member views with optional filters (cache-first)
     * POST /api/members/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched member model views
     */
    async getFilteredViews(filter) {
        const response = await this.getFilteredViewsRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered member views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getFilteredViewsRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    /**
     * Get member views by member type (cache-first)
     * GET /api/members/type/{typeId}/view
     * @param typeId - Member type ID
     * @returns Array of enriched member model views
     */
    async getViewsByType(typeId) {
        const response = await this.getViewsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get member views by type (raw)
     * @param typeId - Member type ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('viewsByType', `type/${typeId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get member views by marital status (cache-first)
     * GET /api/members/marital-status/{statusId}/view
     * @param statusId - Marital status ID
     * @returns Array of enriched member model views
     */
    async getViewsByMaritalStatus(statusId) {
        const response = await this.getViewsByMaritalStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get member views by marital status (raw)
     * @param statusId - Marital status ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByMaritalStatusRaw(statusId) {
        const endpoint = this.getEndpoint('viewsByMaritalStatus', `marital-status/${statusId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
MemberService.DEFAULT_SERVICE_PATH = 'members';
MemberService.BASE_PATH = 'api/members';
//# sourceMappingURL=MemberService.js.map